import orgamax from 'ampersand-app'
import request from '@/modules/base/util/request'
import { urls, getPeriodParams } from '@/modules/taxForms/config'
import { translatePeriod } from '@/enums/period-select.enum'
import { downloadFile } from '@/modules/_helpers/downloadFile'
import moment from 'moment'
import { PERIODS } from '../vatReturn/constants'

export const TaxFormServices = {
  translatePeriod: (period) => {
    return translatePeriod(period)
  },
  getMenuSectionsAsync: async() => {
    return request(urls.getBookedTaxFormModulesUrl(), {
      method: 'GET',
      auth: true,
    })
      .then((res) => {
        return res.body?.data
      })
  },
  /**
   * get the calculated tax form data
   * @param {'ustva'|string} formKey
   * @param {{ startDate: string|number|moment, endDate:string|number|moment }} period
   * @param {Record<string,any>} queryParams
   * @returns {Promise<import('@/modules/taxForms/taxFormTypes').TaxFormMetaData>}
   */
  getTaxFormMetaData: async(formKey, period, queryParams = {}) => {
    return request(urls.getTaxFormMetaDataUrl(formKey), {
      method: 'GET',
      auth: true,
      data: {
        ...getPeriodParams(period),
        ...queryParams,
      },
    })
      .then((res) => {
        return res.body?.data
      })
  },
  saveTaxForm: async(transferData) => {
    return request(urls.saveTaxFormUrl(), {
      method: 'POST',
      auth: true,
      data: transferData,
    })
      .then((res) => {
        return res.body?.data
      })
  },
  saveAndDownloadElsterXml: async(transferData) => {
    return TaxFormServices.saveTaxForm(transferData)
      .then((saveResult) => {
        return TaxFormServices.downloadElsterXml(saveResult.id, false)
      })
  },
  /**
   * @returns {Promise<import('./taxFormTypes').TaxFormEricResponse>}
   */
  validateByEric: async(transferData) => {
    return request(urls.validateTaxFormUrl(), {
      method: 'POST',
      auth: true,
      data: transferData,
    })
      .then((res) => {
        return res.body?.data
      })
  },
  /**
   * @param {string} ticketId
   * @returns {Promise<import('./taxFormTypes').TaxDocumentResponse>}
   */
  getTicketResponse: async(ticketId) => {
    return request(urls.getTicketResponseUrl(ticketId), {
      method: 'GET',
      auth: true,
    })
      .then((res) => {
        return res.body?.data
      })
  },
  /**
   * @returns {Promise<import('./taxFormTypes').TaxFormEricResponse>}
   */
  sendByEric: async(transferData) => {
    return request(urls.sendTaxFormUrl(), {
      method: 'POST',
      auth: true,
      data: transferData,
    })
      .then((res) => {
        return res.body?.data
      })
  },
  getTaxFormList: async(taxFormKey) => {
    return request(urls.getTaxFormListUrl(), {
      method: 'GET',
      auth: true,
      data: { formkey: taxFormKey },
    })
      .then((res) => {
        return res.body?.data
      })
  },
  getLatestCreatedTaxForm: async(taxFormKey) => {
    return request(urls.getLatestCreatedTaxFormUrl(taxFormKey), {
      method: 'GET',
      auth: true,
    })
      .then((res) => {
        return res.body?.data
      })
  },
  getElsterXml: async(taxFormId, isLegacy) => {
    return request(urls.getElsterXmlUrl(taxFormId), {
      method: 'GET',
      auth: true,
      data: isLegacy != null ? { isLegacy } : {},
    })
      .then((res) => {
        return res.body?.data
      })
  },
  createElsterXml: async(taxFormKey, period, year, dataSource, transferToBuhlArchiv = false) => {
    return request(urls.getElsterXmlUrl(taxFormKey), {
      method: 'PUT',
      auth: true,
      data: { year, dataSource, transferToBuhlArchiv },
    }).then((res) => {
      const data = res.body?.data
      if (transferToBuhlArchiv) { return data }

      if (data) {
        const fileName = `${moment().format('YYYY-MM-DD')}_${taxFormKey}-${year}${period !== PERIODS.YEAR
          ? '-' + TaxFormServices.translatePeriod(period)
          : ''
        }.xml`
        startDownload(data, 'text/xml', fileName)
      }

      return data
    })
  },
  getEuerExportHistory: async() => {
    const response = await request(`${urls.resource}/euer/exports`, {
      auth: true,
      isConcurrent: true,
      method: 'GET',
    })

    return response.body?.data
  },
  downloadElsterXml: async(taxFormId, isLegacy) => {
    return TaxFormServices.getElsterXml(taxFormId, isLegacy)
      .then((data) => {
        if (data) {
          const fileName = `${data.formKey}-${data.year}-${TaxFormServices.translatePeriod(data.period)}.xml`
          startDownload(data.xml, 'text/xml', fileName)
        }

        return data
      })
  },
  /**
   * @param {string} formKey
   * @returns {Promise<number[]>}
   */
  getAvailableYears: async(formKey) => {
    return request(urls.getAvailableYearsUrl(formKey), {
      method: 'GET',
      auth: true,
    })
      .then((res) => {
        return res.body?.data || []
      })
  },
  createTaxFormProtocol: async(formKey, data) => {
    return fetch(urls.createTaxFormProtocolUrl(formKey), {
      method: 'POST',
      headers: {
        authorization: `Bearer ${orgamax.user.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        reportValues: data,
      }),
    })
      .then(async(res) => {
        if (!res.ok) {
          const json = await res.json()
          throw new Error(json.message)
        }
        return res.blob()
      })
  },
  downloadTaxFormProtocol: async(id, fileName) => {
    return downloadFile(urls.getTaxFormProtocolUrl(id), fileName)
  },
  getTaxFormProtocol: async(id) => {
    return fetch(urls.getTaxFormProtocolUrl(id), {
      method: 'GET',
      headers: {
        authorization: `Bearer ${orgamax.user.token}`,
        'Content-Type': 'application/json',
      },
    })
      .then(async(res) => {
        if (!res.ok) {
          const json = await res.json()
          throw new Error(json.message)
        }
        return res.blob()
      })
  },

  downloadEuerXml: async(id) => {
    const response = await request(`${urls.resource}/euer/${id}/xml`, {
      method: 'GET',
      auth: true,
      isConcurrent: true,
    })

    const { body: { data: { xml, fileName } } } = response
    return startDownload(xml, 'text/xml', fileName)
  },
}

const startDownload = (data, contentType, fileName) => {
  if (data && contentType && fileName) {
    const blob = new Blob([data], { type: contentType })
    const downloadUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    a.remove()
  }
}
